import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { CalendarDays, Star } from "lucide-react";
import ResponsibleBanner from "@/components/ResponsibleBanner";

function fmt(iso) {
  try {
    const d = new Date(iso);
    const day = d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" });
    const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "UTC" });
    return `${day} · ${time} ET`;
  } catch {
    return "";
  }
}

function Chip({ team, score, showScore, win }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2 min-w-0">
        {team.logo ? (
          <span className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center border border-white/10 overflow-hidden shrink-0"><img src={team.logo} alt={team.abbr} className="w-5 h-5 object-contain" /></span>
        ) : (
          <span className="w-7 h-7 rounded-full flex items-center justify-center text-[9px] font-black text-white shrink-0" style={{ backgroundColor: team.color }}>{team.abbr}</span>
        )}
        <span className={`truncate text-sm font-semibold ${win ? "text-white" : "text-slate-300"}`}>{team.name}</span>
      </div>
      {showScore && <span className={`font-mono-num font-bold ${win ? "text-smasher-gold" : "text-slate-400"}`}>{score}</span>}
    </div>
  );
}

function GameCard({ g }) {
  const final = g.status === "final";
  const live = g.status === "live";
  return (
    <div className="panel panel-hover p-4 flex flex-col gap-2" data-testid={`sched-game-${g.id}`}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-bold tracking-widest text-smasher-gold flex items-center gap-1">
          {g.ranked && <Star className="w-3 h-3 fill-smasher-gold text-smasher-gold" />} {g.league} · WK {g.week}
        </span>
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${final ? "bg-slate-700/50 text-slate-300 border-slate-600" : live ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : "bg-smasher-gold/15 text-smasher-gold border-smasher-gold/30"}`}>
          {g.status.toUpperCase()}
        </span>
      </div>
      <div className="space-y-1.5">
        <Chip team={g.away} score={g.away_score} showScore={final} win={final && g.away_score > g.home_score} />
        <div className="text-[9px] text-slate-600 font-bold text-center">@</div>
        <Chip team={g.home} score={g.home_score} showScore={final} win={final && g.home_score >= g.away_score} />
      </div>
      {(g.line || g.over_under) && (
        <div className="flex items-center gap-2 border-t border-white/5 pt-2">
          {g.line && <span className="text-xs font-mono-num font-bold text-smasher-gold bg-smasher-gold/10 px-2 py-0.5 rounded">{g.line}</span>}
          {g.over_under && <span className="text-[11px] font-mono-num text-slate-400">O/U {String(g.over_under).replace(/^O\/U\s*/i, "")}</span>}
        </div>
      )}
      <div className="border-t border-white/5 pt-2 text-[11px] text-slate-500 flex items-center gap-1">
        <CalendarDays className="w-3.5 h-3.5" /> {fmt(g.start)}{g.venue ? ` · ${g.venue}` : ""}
      </div>
    </div>
  );
}

export default function SchedulePage() {
  const [league, setLeague] = useState("NFL");
  const [data, setData] = useState(null);
  const [week, setWeek] = useState(null);

  const load = useCallback(async (lg) => {
    const { data } = await api.get("/schedule", { params: { league: lg } });
    setData(data);
    setWeek(data.current_week);
  }, []);

  useEffect(() => { load(league); }, [league, load]);

  const weeks = data?.weeks || [];
  const active = weeks.find((w) => w.week === week) || weeks[0];

  return (
    <div className="max-w-[1300px] mx-auto px-4 sm:px-6 py-12 space-y-8">
      <div>
        <div className="text-xs font-bold tracking-[0.2em] text-smasher-orange mb-1">FULL SCHEDULE</div>
        <h1 className="font-heading font-black uppercase text-4xl sm:text-5xl text-gradient-gold flex items-center gap-3"><CalendarDays className="w-9 h-9 text-smasher-gold" /> Football Schedule</h1>
        <p className="text-slate-400 mt-2 text-sm">NFL by week, plus ranked NCAA matchups &amp; results. {data?.last_updated ? `Last updated ${new Date(data.last_updated).toLocaleDateString()}.` : ""}</p>
      </div>

      <div className="flex gap-2">
        {["NFL", "NCAA"].map((lg) => (
          <button key={lg} onClick={() => setLeague(lg)} className={`px-5 py-2 rounded-full text-sm font-bold ${league === lg ? "btn-gold" : "btn-ghost-gold"}`} data-testid={`sched-league-${lg}`}>{lg}</button>
        ))}
      </div>

      {weeks.length === 0 ? (
        <div className="panel p-10 text-center text-slate-500" data-testid="sched-empty">
          No {league} games posted yet. {league === "NCAA" ? "Ranked matchups and scores appear here once posted." : "The weekly schedule appears here as it's posted."}
        </div>
      ) : (
        <>
          <div className="flex gap-2 flex-wrap no-scrollbar">
            {weeks.map((w) => (
              <button key={w.week} onClick={() => setWeek(w.week)} className={`px-4 py-1.5 rounded-lg text-sm font-bold ${active?.week === w.week ? "bg-smasher-gold/20 text-smasher-gold border border-smasher-gold/40" : "bg-black/40 text-slate-400 border border-white/5 hover:text-white"}`} data-testid={`sched-week-${w.week}`}>
                WEEK {w.week}
              </button>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {(active?.games || []).map((g) => <GameCard key={g.id} g={g} />)}
          </div>
        </>
      )}

      <ResponsibleBanner />
    </div>
  );
}
